import * as ImagePicker from 'expo-image-picker';
import { manipulateAsync, SaveFormat } from 'expo-image-manipulator';
import * as tf from '@tensorflow/tfjs';
import * as nsfwjs from 'nsfwjs';
import { HIDE_LOADING_MODAL, SHOW_LOADING_MODAL } from '@reduxLocal/actions/types';
import { store } from '@reduxLocal/persistState';

// Singleton for caching loaded model
let cachedModel: any | null = null;

// Load NSFW model (singleton)
const loadNSFWModel = async () => {
  if (!cachedModel) {
    await tf.ready();
    try {
      await tf.setBackend('webgl');
    } catch {
      await tf.setBackend('cpu');
      console.log('CPU backend set successfully');
    }

    cachedModel = await nsfwjs.load(); // Web model loading
    console.log('NSFW model loaded and cached successfully');
  } else {
    console.log('Using cached NSFW model');
  }
  return cachedModel;
};

// Convert image ArrayBuffer to Tensor (web only)
async function imageToTensor(rawImageData: ArrayBuffer) {
  const blob = new Blob([rawImageData], { type: 'image/jpeg' });
  const imageUrl = URL.createObjectURL(blob);
  const img = new Image();
  img.crossOrigin = 'anonymous';

  return new Promise<tf.Tensor3D>((resolve, reject) => {
    img.onload = () => {
      try {
        const tensor = tf.browser.fromPixels(img).expandDims(0);
        URL.revokeObjectURL(imageUrl);
        resolve(tensor);
      } catch (err) {
        URL.revokeObjectURL(imageUrl);
        reject(err);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(imageUrl);
      reject(new Error('Failed to load image'));
    };
    img.src = imageUrl;
  });
}

// Main handler (web only)
const handleImageUpload = async (local: boolean = false) => {
  const dispatch = store.dispatch;

  try {
    const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (status !== 'granted') {
      alert('Camera roll permissions required!');
      return null;
    }

    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      allowsEditing: true,
      aspect: [4, 3],
      base64: !local,
    });

    if (result.canceled) return null;

    dispatch({ type: SHOW_LOADING_MODAL });

    const resizedImage = await manipulateAsync(
      result.assets[0].uri,
      [{ resize: { width: 224, height: 224 } }],
      { compress: 1, format: SaveFormat.JPEG, base64: !local }
    );

    try {
      const model = await loadNSFWModel();

      const imageResponse = await fetch(resizedImage.uri);
      const rawImageData = await imageResponse.arrayBuffer();

      const imageTensor = await imageToTensor(rawImageData);

      const predictions = await model.classify(imageTensor);
      imageTensor.dispose();

      const nsfwContent = predictions.some(
        (pred: any) =>
          ['Porn', 'Sexy', 'Hentai'].includes(pred.className) && pred.probability > 0.7
      );

      if (nsfwContent) {
        dispatch({ type: HIDE_LOADING_MODAL });
        alert('NSFW content detected! Please choose another image.');
        return null;
      }

      dispatch({ type: HIDE_LOADING_MODAL });
      return local ? result.assets[0] : resizedImage.base64;
    } catch (error) {
      dispatch({ type: HIDE_LOADING_MODAL });
      console.error('Error in image classification:', error);
      alert('Error processing the image.');
      return null;
    }
  } catch (error) {
    dispatch({ type: HIDE_LOADING_MODAL });
    console.error('Error in image upload:', error);
    alert('Error accessing camera roll.');
    return null;
  }
};

export default handleImageUpload;
